import { Money } from '@principle-theorem/accounting';
import {
  IAccountCredit,
  IAccountCreditExtendedData,
  IInteractionV2,
  IInvoice,
  IPatient,
  IPractice,
  IStaffer,
  type ITransaction,
  InvoiceStatus,
  InvoiceType,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  doc,
  DocumentReference,
  Firestore,
  type INamedDocument,
  type Timestamp,
  type Transaction as FirebaseTransaction,
  type WithRef,
} from '@principle-theorem/shared';
import { type DepositPaymentProvenance } from './deposit-invoice';
import { createInvoice } from '../invoice/create-invoice';
import { CANCELLATION_FEE, feeToLineItem } from '../invoice/custom-line-items';
import { Invoice } from '../invoice/invoice';
import { Patient } from '../patient/patient';
import { Transaction } from '../transaction/transaction';

export interface IDepositForfeitureInvoiceReferences {
  invoiceRef: DocumentReference<IInvoice>;
  interactionRef: DocumentReference<IInteractionV2>;
}

export enum DepositForfeitureFundingSource {
  /**
   * A cancellation-cover hold. No money existed until the cancellation, so the
   * captured card settles the fee directly and no credit is ever minted.
   */
  Card = 'card',
  /**
   * A deposit the patient already paid. That money is sitting as account
   * credit, so the fee is settled by consuming it.
   */
  Credit = 'credit',
}

export interface IDepositForfeitureCardFunding {
  source: DepositForfeitureFundingSource.Card;
  /** Traces the payment back to the PaymentIntent that was captured. */
  provenance: DepositPaymentProvenance;
}

export interface IDepositForfeitureCreditFunding {
  source: DepositForfeitureFundingSource.Credit;
  /**
   * Recorded on the payment transaction's `extendedData.accountCreditsUsed` so
   * invoice reporting and refunds treat the forfeiture like any checkout
   * credit payment. This method never mutates the credit itself — the caller
   * owns the single `used` increment.
   */
  credit: WithRef<IAccountCredit>;
}

export type DepositForfeitureFunding =
  | IDepositForfeitureCardFunding
  | IDepositForfeitureCreditFunding;

interface IForfeiturePaymentContext {
  amount: number;
  invoice: WithRef<IInvoice>;
  practiceRef: DocumentReference<IPractice>;
  paidAt: Timestamp;
}

export interface ICreateDepositForfeitureInvoiceParams {
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  /** Where the money settling this fee comes from. */
  funding: DepositForfeitureFunding;
  /**
   * Cents in, converted to dollars at the line-item/transaction boundary to
   * match the forfeiture flow's cents-denominated amounts.
   */
  amountCents: number;
  paidAt: Timestamp;
  /**
   * The practitioner the fee is credited to. Absent leaves the fee with the
   * practice, which income reporting counts as unallocated.
   */
  attributedTo?: INamedDocument<IStaffer>;
  transaction: FirebaseTransaction;
  references?: IDepositForfeitureInvoiceReferences;
}

/**
 * Owns the Cancellation Fee invoice raised when a deposit is forfeited: the
 * invoice, its interaction, and the account-credit payment transaction that
 * settles it. All writes ride the caller's forfeiture transaction because a
 * fee invoice without its credit payment (or vice versa) misstates what the
 * patient owes.
 *
 * Like `DepositInvoice`, this deliberately bypasses
 * `AccountingFunctions.addTransactionToInvoice` so the ledger entry commits
 * atomically with the forfeiture. Consequently it skips that flow's
 * status-change interaction, due-credit upsert (no credit is created here),
 * and claim handling (fee invoices have no claims).
 */
export class DepositForfeitureInvoice {
  /**
   * Creates the paid Cancellation Fee invoice and the payment that settles it
   * inside the caller's transaction, returning the invoice ref for the
   * forfeiture outcome. When funded by credit this deliberately does NOT
   * increment `credit.used`: the forfeiture flow already burns the credit with
   * its `DepositForfeiture` usage-history entry, and incrementing here too
   * would double-spend the deposit.
   */
  static async create(
    params: ICreateDepositForfeitureInvoiceParams
  ): Promise<DocumentReference<IInvoice>> {
    const {
      patient,
      practice,
      funding,
      amountCents,
      paidAt,
      attributedTo,
      transaction,
      references,
    } = params;
    const { invoiceRef, interactionRef } =
      DepositForfeitureInvoice._resolveReferences(patient, references);
    const amount = Money.fromCents(amountCents);
    const lineItem = feeToLineItem(CANCELLATION_FEE, amount, attributedTo);
    const invoice = await createInvoice(
      patient,
      practice,
      undefined,
      [lineItem],
      InvoiceType.Invoice,
      {
        transaction,
        invoiceRef,
        interactionRef,
        invoiceOverrides: { paidAt },
        interactionOverrides: { practiceRef: practice.ref },
      }
    );

    const paymentTransaction = DepositForfeitureInvoice._buildPayment(funding, {
      amount,
      invoice,
      practiceRef: practice.ref,
      paidAt,
    });
    await Firestore.saveDoc(
      {
        ...paymentTransaction,
        ref: doc(
          Invoice.transactionCol(asReffable(invoiceRef)),
          'deposit-forfeiture-payment'
        ),
      },
      undefined,
      transaction
    );

    Invoice.updateStatus(invoice, InvoiceStatus.Issued, paidAt);
    Invoice.updateStatus(invoice, InvoiceStatus.Paid, paidAt);
    invoice.issuedAt = paidAt;
    invoice.paidAt = paidAt;
    await Firestore.patchDoc(
      invoice.ref,
      {
        status: InvoiceStatus.Paid,
        statusHistory: invoice.statusHistory,
        issuedAt: invoice.issuedAt,
        paidAt: invoice.paidAt,
      },
      transaction
    );

    return invoice.ref;
  }

  /**
   * A card-funded fee is settled by the capture itself, so the transaction
   * carries the provider's own reference and no credit provenance — there is
   * no credit to point at. A credit-funded fee keeps the account-credit shape
   * the checkout ledger already understands.
   */
  private static _buildPayment(
    funding: DepositForfeitureFunding,
    context: IForfeiturePaymentContext
  ): ITransaction<IAccountCreditExtendedData> | ITransaction {
    const commonFields = {
      amount: context.amount,
      type: TransactionType.Incoming,
      status: TransactionStatus.Complete,
      from: context.invoice.to.name,
      to: context.invoice.from.name,
      practiceRef: context.practiceRef,
      createdAt: context.paidAt,
    };
    if (funding.source === DepositForfeitureFundingSource.Card) {
      return Transaction.init({
        ...commonFields,
        provider: funding.provenance.provider,
        reference: funding.provenance.reference ?? context.invoice.reference,
      });
    }
    return Transaction.init<IAccountCreditExtendedData>({
      ...commonFields,
      ...Transaction.internalReference(TransactionProvider.AccountCredit),
      extendedData: {
        accountCreditsUsed: [
          {
            name: funding.credit.description,
            ref: funding.credit.ref,
            amount: context.amount,
            refunded: 0,
            totalRefunded: 0,
          },
        ],
      },
    });
  }

  private static _resolveReferences(
    patient: WithRef<IPatient>,
    references?: IDepositForfeitureInvoiceReferences
  ): IDepositForfeitureInvoiceReferences {
    const invoiceRef =
      references?.invoiceRef ?? doc(Patient.invoiceCol(patient));
    const interactionRef =
      references?.interactionRef ??
      doc(Invoice.interactionCol(asReffable(invoiceRef)));
    return { invoiceRef, interactionRef };
  }
}
